/** Crop development period row (dictionaries CropPeriod). */
export interface CropPhaseLike {
  dayStart?: number | null;
  dayEnd?: number | null;
}

export function parsePlantingBoundaryDate(
  plantingDate?: string | Date | null,
  plantingDatePlan?: string | Date | null,
): Date | null {
  const raw = plantingDate ?? plantingDatePlan;
  if (!raw) {
    return null;
  }
  const d = raw instanceof Date ? new Date(raw) : new Date(String(raw));
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  d.setHours(0, 0, 0, 0);
  return d;
}

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function daysSincePlanting(planting: Date, reference: Date): number {
  const plant = startOfDay(planting);
  const ref = startOfDay(reference);
  return Math.floor((ref.getTime() - plant.getTime()) / 86_400_000);
}

export function sortCropPhases<T extends CropPhaseLike>(phases: T[]): T[] {
  return [...phases].sort((a, b) => (a.dayStart ?? 0) - (b.dayStart ?? 0));
}

export function vegetationPeriodEndDay(phases: CropPhaseLike[]): number | null {
  if (!phases.length) {
    return null;
  }
  return Math.max(...phases.map(p => p.dayEnd ?? p.dayStart ?? 0));
}

/**
 * Day offset from planting for norm / timeline alignment.
 * If the vegetation period is already over, returns the last stage end day.
 */
export function resolveVegetationDayOffset(
  phases: CropPhaseLike[],
  planting: Date | null,
  reference: Date = new Date(),
): number | null {
  if (!planting) {
    return null;
  }
  const offset = daysSincePlanting(planting, reference);
  const endDay = vegetationPeriodEndDay(phases);
  if (endDay != null && offset > endDay) {
    return endDay;
  }
  return offset;
}

/**
 * Index of the active vegetation stage for `reference` (today or selected date).
 * If the period is past the last stage, returns the last stage index.
 */
export function resolveActiveCropPhaseIndex(
  phases: CropPhaseLike[],
  planting: Date | null,
  reference: Date = new Date(),
): number | null {
  if (!phases.length || !planting) {
    return null;
  }
  const sorted = sortCropPhases(phases);
  const offset = daysSincePlanting(planting, reference);
  const lastIdx = sorted.length - 1;
  const lastEnd = sorted[lastIdx].dayEnd ?? sorted[lastIdx].dayStart ?? 0;

  if (offset > lastEnd) {
    return lastIdx;
  }

  for (let i = 0; i < sorted.length; i++) {
    const start = sorted[i].dayStart ?? 0;
    const end = sorted[i].dayEnd ?? start;
    if (offset >= start && offset <= end) {
      return i;
    }
  }

  return null;
}

export function isCropPhaseActive(
  phases: CropPhaseLike[],
  phaseIndex: number,
  planting: Date | null,
  reference: Date = new Date(),
): boolean {
  return resolveActiveCropPhaseIndex(phases, planting, reference) === phaseIndex;
}
